// TODO: Update this to use proper error objects or AbortControllers
/* eslint-disable @typescript-eslint/prefer-promise-reject-errors */
import { useRef, useEffect } from "react";

/**
 * This is a hook I've created to allow us to cancel API calls automatically if components they are used in are unmounted.
 * It should be added anywhere async calls are used inside of useEffects! -- CC
 */

/*
  Example Use - does not need the addition of a return function in the useEffect as the hook will call that automatically from within the about call it's passed into it.

  ** Import the hook somewhere near the top of your component (where we typically import hooks)
  const { cancellablePromise } = useCancellablePromise();

  ** If using the AmplifyAPIWrapper, pass the hook into the function as shown below, If no notification are being passed in, you must pass that parameter as null before adding the cancellablePromise hook.
  useEffect(() => {
    const getAircraftAction = async (): Promise<void> => {
      const notifications: INotifications = { dispatch, errorMessage: "No Aircraft found." };
      const response = await AmplifyAPIWrapper.Get<IAircraft[]>("/aircraft", notifications, cancellablePromise);
      setIsLoading(false);
      if (Array.isArray(response)) setAircraft(response);
    };
    getAircraftAction();

  !! IMPORTANT - you must remove the hook from the dependency array. it will still work fine but shouldn't be in here as it will cause the call to be repeated continously.
  ** Since the hook is only initialized once and will never change, it doesn't need to be passed into the dependency array.
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  ** If using another async function, simply pass that function as a paramter into the hook like shown.
  const response = await cancellablePromise(getAPISParties());

Additional notes:  the exhaustive deps warning - The above eslint rule could be disabled in the .eslintrc.json file by adding the following...
but this would be dangerous as we do have lots of hooks used in useEffects that should trigger re-renders. - CC
{
  "plugins": [
    "react-hooks"
  ],
  "rules": {
    "react-hooks/rules-of-hooks": "error",
    "react-hooks/exhaustive-deps": "off"
  }
}

 */

interface IMakeCancellable {
  promise: Promise<any>;
  cancel: () => void;
}

export function makeCancelable(promise: Promise<any>): IMakeCancellable {
  let isCanceled = false;
  const wrappedPromise = new Promise((resolve, reject) => {
    promise
      .then((val) => (isCanceled ? reject({ isCanceled }) : resolve(val)))
      .catch((error) => (isCanceled ? reject({ isCanceled }) : reject(error)));
  });
  return {
    promise: wrappedPromise,
    cancel() {
      isCanceled = true;
    },
  };
}

interface ICancellableHook {
  cancellablePromise: (p: Promise<any>) => Promise<any>;
}

export function useCancellablePromise(cancelable = makeCancelable): ICancellableHook {
  const emptyPromise = Promise.resolve(true);

  // test if the input argument is a cancelable promise generator
  if (cancelable(emptyPromise)?.cancel === undefined) {
    throw new Error("promise wrapper argument must provide a cancel() function");
  }

  const promises: any = useRef();

  useEffect(() => {
    promises.current = promises.current || [];
    return function cancel() {
      promises.current.forEach((p: IMakeCancellable) => p.cancel());
      promises.current = [];
    };
  }, []);

  function cancellablePromise(p: Promise<any>): Promise<any> {
    const cPromise = cancelable(p);
    promises.current.push(cPromise);
    return cPromise.promise;
  }

  return { cancellablePromise };
}
